import React, {useState} from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import InventoryStructuredTableMode, {
  InventoryTableInsertionTableType,
} from 'notebook/integrations/samples/InventoryStructuredTableMode';
import {
  InventoryStructuredTableSchemaType,
  InventoryTableMetadata,
} from 'notebook/structured-tables/StructuredTableConstants';
import RegistrySelector from 'registry/RegistrySelector';
import AllStorablesSchemaSelector from 'samples/AllStorablesSchemaSelector';
import {
  ContainerSchema,
  ParentStorableType,
  PlateSchema,
  PlateSchemaType,
} from 'samples/storables/StorableSchemaConstants';
import {isSchemaTypeFilterOption} from 'schemas/SchemaConstants';
import * as SchemaPredicates from 'schemas/SchemaPredicates';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import APIUtil from 'util/APIUtil';

export type UpdateContainersStructuredTableInsertionConfiguration =
  | {
      tableType: InventoryTableInsertionTableType.UPDATE_CONTAINERS;
      metadata: InventoryTableMetadata;
      storableSchema: PlateSchema | ContainerSchema | null;
      plateWellSchema: ContainerSchema | null;
    }
  | 'INVALID';

function getMatchingPlateWellSchema(
  plateSchema: PlateSchema,
  containerSchemas: Array<ContainerSchema>
): ContainerSchema | null {
  return (
    containerSchemas.find((containerSchema) => containerSchema.id === plateSchema.containerSchemaId) ?? null
  );
}

function standardizeStorableSchemaType(storableSchemaType: string): InventoryStructuredTableSchemaType {
  if (storableSchemaType === InventoryStructuredTableSchemaType.CONTAINER) {
    return InventoryStructuredTableSchemaType.CONTAINER;
  } else if (storableSchemaType === InventoryStructuredTableSchemaType.PLATE) {
    return InventoryStructuredTableSchemaType.PLATE;
  }
  assert(false, `Unexpected storable schema type ${storableSchemaType}`);
}

// Keep in sync with benchling/notebook/structured_tables/instantiators/inventory_table_instantiator.py
export default function UpdateContainersStructuredTableInsertForm({
  onChange,
}: {
  onChange: (newInsertionConfig: UpdateContainersStructuredTableInsertionConfiguration) => void;
}): JSX.Element {
  const [selectedSchemaId, setSelectedSchemaId] = useState<string | null>(null);
  const [containerSchemas, setContainerSchemas] = useState<Array<ContainerSchema>>([]);
  const [registrySelector, setRegistrySelector] = useState<{registryId: string} | null>(null);
  const [savedInsertionConfig, setSavedInsertionConfig] =
    useState<UpdateContainersStructuredTableInsertionConfiguration>('INVALID');

  return (
    <FormGroup
      dataTestAttrs={{
        component: 'InventoryUpdateTableInsertModal',
        element: 'form',
        key: 'schemaSelector',
      }}
    >
      <Label>Schema</Label>
      <AllStorablesSchemaSelector
        onChange={(schemaOrFilterOption) => {
          const newSchema = isSchemaTypeFilterOption(schemaOrFilterOption) ? null : schemaOrFilterOption;
          setSelectedSchemaId(schemaOrFilterOption.id);
          let newPlateWellSchema: ContainerSchema | null = null;
          if (newSchema && SchemaPredicates.isPlateSchema(newSchema)) {
            newPlateWellSchema = getMatchingPlateWellSchema(newSchema, containerSchemas);
          }

          const schemaType = isSchemaTypeFilterOption(schemaOrFilterOption)
            ? schemaOrFilterOption.schemaType
            : APIUtil.apiIdToMaybeType(schemaOrFilterOption.id);
          if (schemaType) {
            const defaultRegistryId = containerSchemas[0]?.registryId;
            const singleRegistry = containerSchemas.every(
              (schema) => schema.registryId === defaultRegistryId
            );
            if (!singleRegistry && newSchema == null) {
              setRegistrySelector({registryId: defaultRegistryId});
            } else {
              setRegistrySelector(null);
            }
            const fallbackRegistryId = newSchema?.registryId ?? defaultRegistryId ?? null;
            const storableSchemaType = standardizeStorableSchemaType(schemaType);
            const newInsertionConfig: UpdateContainersStructuredTableInsertionConfiguration = {
              tableType: InventoryTableInsertionTableType.UPDATE_CONTAINERS,
              metadata: {
                canChangeUnits: false,
                searchAllSchemas: true,
                mode: InventoryStructuredTableMode.UPDATE_CONTAINERS,
                storableSchemaType,
                fallbackRegistryId,
                parentStorableType:
                  storableSchemaType === InventoryStructuredTableSchemaType.CONTAINER
                    ? ParentStorableType.GRID_OR_LOCATION
                    : ParentStorableType.LOCATION,
              },
              storableSchema: newSchema,
              plateWellSchema: newPlateWellSchema,
            };
            setSavedInsertionConfig(newInsertionConfig);
            onChange(newInsertionConfig);
          }
        }}
        selectedAllStorablesSchemaId={selectedSchemaId}
        includeStorableTypes={[ModelTypeConstants.CONTAINER, ModelTypeConstants.PLATE]}
        saveStorableSchemas={(newStorableSchemas) => {
          const newContainerSchemas: Array<ContainerSchema> = newStorableSchemas.filter((schema) =>
            SchemaPredicates.isContainerSchema(schema)
          );
          if (newContainerSchemas.length !== containerSchemas.length) {
            setContainerSchemas(newContainerSchemas);
          }
        }}
        plateSchemaType={PlateSchemaType.FIXED_PLATE}
        shouldShowNoSchemaOption={false}
        shouldShowAnySchemaOfTypeOptions={true}
      />
      {registrySelector != null && (
        <>
          <br />
          <Label>Registry</Label>
          <RegistrySelector
            registryId={registrySelector.registryId}
            onChange={(e) => {
              setRegistrySelector(e.target.value ? {registryId: e.target.value.id} : null);
              if (savedInsertionConfig !== 'INVALID') {
                const newInsertionConfig = {
                  ...savedInsertionConfig,
                  metadata: {
                    ...savedInsertionConfig.metadata,
                    fallbackRegistryId: e.target.value?.id ?? null,
                  },
                };
                setSavedInsertionConfig(newInsertionConfig);
                onChange(newInsertionConfig);
              }
            }}
          />
        </>
      )}
    </FormGroup>
  );
}
